import { supabase } from "../supabaseClient";

/**
 * BrokerageService
 * ----------------
 * Derives the CP "Basic Brokerage" for a lead's allotments **as per the Benefit Plan**,
 * replacing the old hard-coded 2%/3%-by-date logic on the lead card.
 *
 * Hybrid strategy (see Documentation/CP_BasicBrokerage_AsPerBenefitPlan_REQUIREMENT.md):
 *   - Option A: read the confirmed value already computed by the backend into `CpPayments`.
 *   - Option B: when no payment entry exists yet (allotment -> agreement window), OR the stored
 *               entry is stale, recalculate live from `base_brokerage_plans` (mirrors the backend
 *               `fetchBrokeragePlan` + `calculateBrokerage` in cppayments/cpSchedular.js).
 *
 * Staleness guard: `CpPayments.Agreement_Value` is the value the row was computed on
 * (backend sources it from `unitCost.bsp`). If it differs from the allotment's current bsp,
 * the stored row is stale, so we show the live plan value flagged as "revised".
 *
 * Defaults for the open business questions (requirement §10 — adjust when Finance confirms):
 *   - Amount = pre-tax "Basic" only (Brokerage_Amount_Basic), matching the card's current meaning.
 *   - Rounding = Math.ceil for live estimates (matches previous card behaviour),
 *                Math.round for the confirmed stored value.
 *   - Merged units = use unit1's wing, else fall back to project-level plan.
 */

/* ----------------------------- helpers ----------------------------- */

const toNum = (v) => Number(v) || 0;

const getProjectId = (a) =>
  a?.unitId?.projectId?.projectId ??
  a?.inventoryMergedId?.projectId?.projectId ??
  null;

const getWingId = (a) =>
  a?.unitId?.wingId?.wingId ??
  a?.inventoryMergedId?.unit1Id?.wingId?.wingId ??
  null;

// Effective date used to pick the plan window before an agreement date exists.
const getEffectiveDateEpoch = (a) => {
  const raw = a?.preselectConfirmationDate || a?.created_at;
  const ts = Number(new Date(raw));
  return Number.isNaN(ts) ? Number(new Date()) : ts;
};

// Backend computes brokerage on the agreement value, which it sources from unitCost.bsp.
const getAgreementValue = (a) => toNum(a?.unitCost?.bsp);

const calcFromPlan = (plan, agreementValue) => {
  if (!plan) return null;
  if (plan.calc_type === "Percentage")
    return Math.ceil((toNum(agreementValue) * toNum(plan.value)) / 100);
  if (plan.calc_type === "Fixed") return Math.ceil(toNum(plan.value));
  return null;
};

/* --------------------- Option B: live plan lookup --------------------- */

// Fetch (and cache within a single run) the base-brokerage plans linked to a project/wing.
const fetchPlansForProject = async (projectId, wingId, cache) => {
  const key = `${projectId}|${wingId ?? "null"}`;
  if (cache.has(key)) return cache.get(key);

  let query = supabase
    .from("base_brokerage_plan_projects")
    .select(
      "base_brokerage_plans!inner(id, calc_type, value, start_date, end_date, status, deleted_at)"
    )
    .eq("project_id", projectId)
    .is("deleted_at", null);

  query = wingId ? query.eq("wing_id", wingId) : query.is("wing_id", null);

  const { data, error } = await query;
  const plans =
    error || !data
      ? []
      : data.map((d) => d.base_brokerage_plans).filter(Boolean);

  cache.set(key, plans);
  return plans;
};

// Pick the Active plan whose date window contains `dateEpoch`.
const pickValidPlan = (plans, dateEpoch) =>
  (plans || []).filter(
    (p) =>
      p &&
      p.status === "Active" &&
      !p.deleted_at &&
      Number(p.start_date) <= dateEpoch &&
      (p.end_date === null || Number(p.end_date) >= dateEpoch)
  )[0] || null;

// Wing-level plan first, then project-level fallback (mirrors backend fetchBrokeragePlan).
const resolveBasePlanCached = async (projectId, wingId, dateEpoch, cache) => {
  if (!projectId) return null;
  if (wingId) {
    const wingPlan = pickValidPlan(
      await fetchPlansForProject(projectId, wingId, cache),
      dateEpoch
    );
    if (wingPlan) return wingPlan;
  }
  return pickValidPlan(
    await fetchPlansForProject(projectId, null, cache),
    dateEpoch
  );
};

/** Public: resolve the base-brokerage plan for a single project/wing/date. */
export const resolveBasePlan = (projectId, wingId, dateEpoch) =>
  resolveBasePlanCached(projectId, wingId, dateEpoch, new Map());

/* ------------------------- Agreement dates ------------------------- */

/**
 * Fetch the true Agreement Date (AgreementForm.agreementStatus.agreement_date) for a batch
 * of allotments. Used by LeadCard to decide whether an allotment falls before or after the
 * Benefit Plan cutover date, since AllotmentPayment.created_at is only the Allotment Date.
 * @returns {Object} keyed by allotmentPaymentId -> agreement_date string|null
 */
export const getAgreementDatesForAllotments = async (allotmentPaymentIds = []) => {
  const ids = (allotmentPaymentIds || []).filter(Boolean);
  if (!ids.length) return {};

  const { data, error } = await supabase
    .from("AgreementForm")
    .select("allotmentPaymentId, agreementStatus")
    .in("allotmentPaymentId", ids);

  if (error || !data) return {};

  const map = {};
  data.forEach((row) => {
    if (row.allotmentPaymentId) {
      map[row.allotmentPaymentId] = row.agreementStatus?.agreement_date || null;
    }
  });
  return map;
};

/* --------------------------- Hybrid API --------------------------- */

/**
 * Compute Basic Brokerage for a list of allotments.
 * @returns {Object} keyed by allotmentPaymentId:
 *   { amount: number|null, source: 'confirmed'|'estimate'|'revised'|'none', label: string }
 */
export const getBasicBrokerageForAllotments = async (allotments = []) => {
  const result = {};
  const list = (allotments || []).filter((a) => a && a.allotmentPaymentId);
  if (!list.length) return result;

  // Option A: batch-read existing CpPayments rows for these allotments.
  const paymentMap = {};
  try {
    const { data: payments } = await supabase
      .from("CpPayments")
      .select("allotmentPaymentId, Brokerage_Amount_Basic, Agreement_Value")
      .in(
        "allotmentPaymentId",
        list.map((a) => a.allotmentPaymentId)
      );
    (payments || []).forEach((p) => {
      if (p.allotmentPaymentId) paymentMap[p.allotmentPaymentId] = p;
    });
  } catch (e) {
    console.error("BrokerageService: CpPayments read failed", e);
  }

  const cache = new Map();

  await Promise.all(
    list.map(async (a) => {
      const id = a.allotmentPaymentId;
      const row = paymentMap[id];
      const currentValue = getAgreementValue(a);

      // Confirmed: a payment row exists and was computed on the current value.
      if (
        row &&
        currentValue > 0 &&
        Math.abs(toNum(row.Agreement_Value) - currentValue) < 1
      ) {
        result[id] = {
          amount: Math.round(toNum(row.Brokerage_Amount_Basic)),
          source: "confirmed",
          label: "Basic Brokerage",
        };
        return;
      }

      // Otherwise recalculate live from the plan (Option B).
      const plan = await resolveBasePlanCached(
        getProjectId(a),
        getWingId(a),
        getEffectiveDateEpoch(a),
        cache
      );
      const amount = calcFromPlan(plan, currentValue);

      if (amount === null) {
        result[id] = { amount: null, source: "none", label: "Basic Brokerage" };
      } else if (row) {
        // Stored row exists but is stale (value changed since it was computed).
        result[id] = {
          amount,
          source: "revised",
          label: "Revised – pending update",
        };
      } else {
        result[id] = {
          amount,
          source: "estimate",
          label: "Basic Brokerage",
        };
      }
    })
  );

  return result;
};
